/**
 * PRESENTATION ONLY — labels, grouping, wording and deep links for the Settings shell.
 *
 * Nothing here decides access. Which sections exist for a person, and whether a field is
 * editable, comes from the server manifest (docs/SETTINGS-MODEL.md §3). This file answers the
 * separate question: once the server has said a section is offered, what do we call it and how
 * do we word its read-only state.
 *
 * If a section id arrives that this file does not know, the shell still renders it from the id
 * rather than dropping it — a missing label is a cosmetic bug, but a silently hidden section
 * would be a permissions lie.
 */
import type { ReadOnlyReason } from '@/lib/settingsApi';

/** The four bands the navigation groups sections into. */
export type SettingsGroup = 'Personal' | 'Practice' | 'Working with' | 'Chambers';

export interface SectionMeta {
  label: string;
  group: SettingsGroup;
  /** One line under the section title. */
  description: string;
  /**
   * The working pages where this section's settings are edited TODAY — for the sections that do
   * NOT yet have an editor of their own. Account, Security and Notifications no longer appear
   * here: they are built INTO the shell, and their old standalone pages have been deleted.
   *
   * A section may lead to several pages. The shell replaced a hub of cards, and every card on it
   * led to a working screen — Bill settings (letterhead, logo, invoice header), Email identity,
   * the hearing prep checklist, practice areas, sync health, the audit log, inviting members.
   * Each of those screens must stay reachable from here; `existing-pages.guard` fails if any
   * standalone settings page in App.tsx has no way in.
   *
   * Keyed by actor because the application's existing settings pages are role-scoped and NOT
   * symmetrical: a chambers administrator has a profile page but no privacy page; a clerk has
   * neither a calendar-sharing page nor a privacy page. An entry is present only where the route
   * genuinely exists, so the shell never offers a link that dead-ends.
   */
  pages?: ExistingPage[];
}

export type SettingsActor = 'barrister' | 'clerk' | 'admin' | 'staff';

export interface ExistingPage {
  label: string;
  description: string;
  to: Partial<Record<SettingsActor, string>>;
}

export const SECTION_META: Record<string, SectionMeta> = {
  account: {
    label: 'My Account',
    group: 'Personal',
    description: 'Your name, contact details and how you appear across the platform',
  },
  security: {
    label: 'Security',
    group: 'Personal',
    description: 'Password, sessions, two-factor and how you sign in',
  },
  notifications: {
    label: 'Notifications',
    group: 'Personal',
    description: 'What you are told about, and how it reaches you',
  },
  appearance: {
    label: 'Appearance & Regional',
    group: 'Personal',
    description: 'Theme, density, timezone, date and time format',
  },
  diary: {
    label: 'Diary & Calendar',
    group: 'Personal',
    description: 'Calendar connection, sharing, working hours and diary defaults',
    // Calendar sharing (the iCal feed) keeps its own page — it is a distinct capability with a
    // token to manage, not a preference. The Diary section's editor links to it directly.
    pages: [
      { label: 'Calendar sharing', description: 'Share your diary through a private calendar link', to: { barrister: '/barrister/settings/calendar' } },
    ],
  },
  email: {
    label: 'Email & Communication',
    group: 'Personal',
    description: 'Your mailbox, signatures, templates and reading preferences',
    pages: [
      { label: 'Email identity', description: 'The from name and address on outbound email — fee notes, document sends, intake confirmations', to: { barrister: '/barrister/settings/email-identity' } },
      // Where a mailbox is reconnected or disconnected — DELETE /mailboxes/:id had no button anywhere.
      { label: 'Connected accounts', description: 'See, reconnect or disconnect the mailboxes connected to Jurivoxa', to: { barrister: '/barrister/inbox/settings' } },
      // Kept reachable: its only way in was the Diary "Connect one" link, which now goes to calendar
      // settings where a calendar is actually connected.
      { label: 'Apps & integrations', description: 'Planned connections to other tools your chambers uses — not yet available', to: { barrister: '/barrister/settings/apps' } },
    ],
  },
  privacy: {
    label: 'Privacy & Data',
    group: 'Personal',
    description: 'Your data export and account lifecycle',
    pages: [
      { label: 'Privacy & data', description: 'Data export, account deactivation and retention', to: { barrister: '/barrister/settings/privacy' } },
    ],
  },
  practice: {
    label: 'My Practice',
    group: 'Practice',
    description: 'Fee rates, funding basis, practice areas and your public profile',
    pages: [
      { label: 'Fees & VAT', description: 'Hourly rates, VAT number and payment terms', to: { barrister: '/barrister/settings/fees' } },
      { label: 'Bill settings', description: 'Letterhead, logo, invoice header and footer, bill templates and automated reminders', to: { barrister: '/barrister/settings/billing' } },
      { label: 'Practice areas', description: 'The areas you take instructions in', to: { barrister: '/barrister/settings/areas' } },
      { label: 'Hearing prep checklist', description: 'Your own preparation list shown on every hearing', to: { barrister: '/barrister/settings/prep-checklist' } },
      { label: 'Public profile', description: 'What instructing solicitors and the public can see, and your notable cases', to: { barrister: '/barrister/settings/public-profile' } },
    ],
  },
  relationship: {
    label: 'My Barristers & Access',
    group: 'Working with',
    description: 'Who you support, what you have been granted, and cover arrangements',
  },
  'chambers.profile': {
    label: 'Chambers Profile',
    group: 'Chambers',
    description: 'Name, address, contact details and identification numbers',
    pages: [
      { label: 'Chambers preferences', description: 'Address, fiscal year and identification numbers', to: { barrister: '/barrister/settings/chambers', admin: '/admin/settings/chambers' } },
    ],
  },
  'chambers.branding': {
    label: 'Branding',
    group: 'Chambers',
    description: 'Logo, colour and how invoices are headed',
    pages: [
      { label: 'Bill settings', description: 'Letterhead, logo, invoice header and footer, bill templates and automated reminders', to: { barrister: '/barrister/settings/billing', admin: '/admin/settings/billing' } },
    ],
  },
  'chambers.communication': {
    label: 'Communication',
    group: 'Chambers',
    description: 'The identity your chambers sends from, and its suppression list',
  },
  'chambers.policy': {
    label: 'Policies',
    group: 'Chambers',
    description: 'Diary and financial rules that apply across the practice',
    pages: [
      { label: 'Diary policy', description: 'Reminder timings, travel buffer and booking rules', to: { admin: '/admin/settings/diary-policy' } },
      { label: 'Financial policy', description: 'VAT rate, billing approval and write-off thresholds', to: { admin: '/admin/settings/financial-policy' } },
    ],
  },
  'chambers.people': {
    label: 'People & Access',
    group: 'Chambers',
    description: 'Members, roles and who may do what',
    pages: [
      { label: 'Manage users', description: 'Invite, edit and deactivate members', to: { admin: '/admin/settings/users' } },
      { label: 'Members', description: 'Membership status and tenure', to: { admin: '/admin/members' } },
      { label: 'Roles & permissions', description: 'Who can do what in your chambers', to: { admin: '/admin/settings/roles' } },
    ],
  },
  'chambers.integrations': {
    label: 'Integrations & Health',
    group: 'Chambers',
    description: 'Connected services and the state of every sync',
    pages: [
      { label: 'Calendar sync health', description: 'Connected calendars, stale syncs and failures', to: { admin: '/admin/settings/calendar-health' } },
      { label: 'Email sync health', description: 'Connected mailboxes, last sync, errors and recovery', to: { admin: '/admin/settings/email-health' } },
    ],
  },
  'chambers.billing': {
    label: 'Billing',
    group: 'Chambers',
    description: 'Your plan, renewal and invoices',
  },
  'chambers.governance': {
    label: 'Records & Retention',
    group: 'Chambers',
    description: 'How long records are kept, and the audit trail',
    pages: [
      { label: 'Activity log', description: 'Every change made in your chambers', to: { admin: '/admin/activity-log', barrister: '/barrister/activity-log', clerk: '/clerk/activity-log' } },
    ],
  },
};

/** Group order in the navigation. */
export const GROUP_ORDER: SettingsGroup[] = ['Personal', 'Practice', 'Working with', 'Chambers'];

export function sectionMeta(id: string): SectionMeta {
  return (
    SECTION_META[id] ?? {
      // Unknown section: show it rather than hide it. See the file header.
      label: id,
      group: 'Personal',
      description: '',
    }
  );
}

/**
 * Human-readable labels for each setting key. Absent keys fall back to the key's own last
 * segment, so a newly registered setting appears immediately instead of waiting for a label.
 */
export const SETTING_LABEL: Record<string, string> = {
  'account.name': 'Name',
  'account.displayName': 'Display name',
  'account.jobTitle': 'Title or honorific',
  'account.phone': 'Phone numbers',
  'account.avatar': 'Photo',
  'account.signatureImage': 'Signature image',
  'account.email': 'Email address',
  'account.memberStatus': 'Member type and status',
  'account.myAccess': 'My access',
  'account.bsbNumber': 'BSB authorisation number',
  'account.callYear': 'Year of call',
  'account.inn': 'Inn of Court',
  'account.biography': 'Biography',
  'security.password': 'Password',
  'security.sessions': 'Active sessions',
  'security.mfa': 'Two-factor authentication',
  'security.recoveryCodes': 'Recovery codes',
  'security.loginMethods': 'How you sign in',
  'security.loginHistory': 'Sign-in history',
  'notifications.matrix': 'What you are notified about',
  'notifications.push': 'Browser notifications',
  'notifications.quietHours': 'Quiet hours',
  'appearance.theme': 'Theme',
  'appearance.density': 'Density',
  'appearance.sidebar': 'Sidebar',
  'appearance.timezone': 'Timezone',
  'appearance.dateFormat': 'Date format',
  'appearance.timeFormat': 'Time format',
  'appearance.locale': 'Language',
  'appearance.landingPage': 'Where you land after signing in',
  'diary.calendarConnection': 'Connected calendar',
  'diary.syncStatus': 'Sync status',
  'diary.calendarSelection': 'Which calendars sync',
  'diary.icalSharing': 'Diary sharing link',
  'diary.reminderOverrides': 'Your reminder timings',
  'diary.workingHours': 'Working days and hours',
  'diary.viewPreferences': 'Default diary view',
  'diary.availability': 'Availability',
  'diary.allocationDefaults': 'Booking defaults',
  'email.mailboxConnection': 'Connected mailbox',
  'email.syncHealth': 'Mailbox sync health',
  'email.readingPreferences': 'Reading preferences',
  'email.sendPreferences': 'Sending preferences',
  'email.quietHours': 'Email quiet hours',
  'email.vacationResponder': 'Out of office',
  'email.signatures': 'Signatures',
  'email.templates': 'Templates',
  'email.filters': 'Filters',
  'email.labels': 'Labels',
  'practice.feeRates': 'Fee rates',
  'practice.vatRegistration': 'VAT registration',
  'practice.fundingBasis': 'Funding arrangements accepted',
  'practice.areas': 'Practice areas',
  'practice.billTemplates': 'Bill templates',
  'practice.bankAccounts': 'Bank accounts',
  'practice.publicProfile': 'Public profile',
  'practice.prepChecklist': 'Hearing prep checklist',
  'chambers.name': 'Chambers name',
  'chambers.legalName': 'Legal name',
  'chambers.registrationNo': 'Registration number',
  'chambers.vatNumber': 'VAT number',
  'chambers.contact': 'Contact details',
  'chambers.address': 'Address',
  'chambers.timezone': 'Timezone',
  'chambers.regional': 'Currency and locale',
  'chambers.fiscalYearStart': 'Fiscal year start',
  'chambers.logo': 'Logo',
  'chambers.primaryColor': 'Primary colour',
  'chambers.invoiceHeader': 'Invoice header and footer',
  'chambers.senderIdentity': 'Sending identity',
  'chambers.emailSuppression': 'Suppressed addresses',
  'chambers.diaryPolicy': 'Diary policy',
  'chambers.financialPolicy': 'VAT rate and billing approval',
  'chambers.commissionPolicy': 'Commission and write-off thresholds',
  'chambers.members': 'Members',
  'chambers.roles': 'Roles and permissions',
  'chambers.integrations': 'Connected services',
  'chambers.syncHealth': 'Sync health',
  'chambers.billing': 'Plan and subscription',
  'chambers.invoices': 'Invoices',
  'chambers.retention': 'Retention',
  // The key stays `chambers.auditLog` — it is the server's registry key and must not drift.
  // Only the wording shown to a person changes, matching the page it opens and its /activity-log URL.
  'chambers.auditLog': 'Activity log',
};

export function settingLabel(key: string): string {
  if (SETTING_LABEL[key]) return SETTING_LABEL[key]!;
  const tail = key.split('.').pop() ?? key;
  return tail.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

/**
 * The approved read-only wording (docs/SETTINGS-MODEL.md §5). Read-only is a first-class state:
 * it always names the authority, so a person knows who to ask rather than assuming it is broken.
 */
export function readOnlyWording(reason: ReadOnlyReason, opts?: { grantor?: string | null }): string {
  switch (reason) {
    case 'organisation-managed':
      return 'Managed by your chambers administrator.';
    case 'granted-by-relationship':
      return opts?.grantor
        ? `Your access is granted by ${opts.grantor}. Contact them to change it.`
        : 'Your access is granted by the barristers you support. Contact them to change it.';
    case 'terms-set-by-chambers':
      return 'Your practice terms are set by your chambers.';
    case 'not-editable':
      return 'Kept by the platform — not something anyone edits here.';
  }
}
